import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Navigation } from '../Navigation';
import '../Dashboard.css';
import { getAudit, V2AuditRun, V2AuditEvent } from '../../services/optionsTraderV2Service';
import { RiskDisclaimer } from '../shared/RiskDisclaimer';
import { etDate, engineDisplayName } from './v2Shared';

// TT Options Trader v2 (paper) -- Audit Log. A copy of v1 OptionsAudit.tsx fed by
// GET /v2/audit (one request; runs are grouped server-side, one per decision).

// Mask sensitive values in audit detail JSON (account numbers, credential keys, secrets)
function maskSensitive(obj: any): any {
  if (Array.isArray(obj)) return obj.map(maskSensitive);
  if (obj && typeof obj === 'object') {
    const masked: any = {};
    for (const [k, v] of Object.entries(obj)) {
      if (/^account$|account.*number|accountNumber|credentialKey|credential|secret/i.test(k) && typeof v === 'string') {
        masked[k] = v.length > 4 ? '***' + v.slice(-4) : '****';
      } else {
        masked[k] = maskSensitive(v);
      }
    }
    return masked;
  }
  return obj;
}

type AuditEntry = V2AuditEvent;
type RunSummary = V2AuditRun;

const STATUS_COLORS: Record<string, string> = { success: '#16a34a', failure: '#dc2626', skip: '#6b7280', warning: '#d97706' };

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '–';
  return new Date(iso).toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function fmtDuration(ms: number | null | undefined): string {
  if (!ms) return '';
  return ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`;
}

const RunCard: React.FC<{ run: RunSummary; selected: boolean; onClick: () => void; engineName: string }> = ({ run, selected, onClick, engineName }) => {
  const statusColor = run.errors > 0 ? '#dc2626' : run.trades > 0 ? '#16a34a' : run.skips > 0 ? '#6b7280' : '#3b82f6';
  return (
    <div onClick={onClick} style={{
      background: selected ? '#1e293b' : '#111827', border: selected ? '1px solid #3b82f6' : '1px solid #1f2937',
      borderRadius: 8, padding: '12px 14px', cursor: 'pointer', marginBottom: 8, transition: 'border-color 0.15s',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <span style={{ color: '#e5e7eb', fontSize: 14, fontWeight: 600 }}>{fmtDate(run.startedAt)}</span>
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: statusColor, display: 'inline-block' }} />
      </div>
      <div style={{ display: 'flex', gap: 12, fontSize: 12, color: '#9ca3af' }}>
        {run.symbol && <span style={{ color: '#a78bfa' }}>{run.symbol}</span>}
        <span>{engineName}</span>
        {run.trades > 0 && <span style={{ color: '#16a34a' }}>{run.trades} trade{run.trades !== 1 ? 's' : ''}</span>}
        {run.skips > 0 && <span>{run.skips} skip{run.skips !== 1 ? 's' : ''}</span>}
        {run.errors > 0 && <span style={{ color: '#dc2626' }}>{run.errors} err</span>}
      </div>
    </div>
  );
};

const EventRow: React.FC<{ event: AuditEntry; expanded: boolean; onClick: () => void; symbol?: string | null }> = ({ event, expanded, onClick, symbol }) => (
  <div style={{ marginBottom: 2 }}>
    <div onClick={onClick} style={{
      display: 'flex', alignItems: 'center', gap: 10, padding: '6px 10px', cursor: 'pointer',
      borderRadius: 6, background: expanded ? '#1e293b' : 'transparent', transition: 'background 0.15s',
    }}>
      <span style={{ width: 10, height: 10, borderRadius: '50%', background: STATUS_COLORS[event.status] || '#6b7280', display: 'inline-block', flexShrink: 0 }} />
      <span style={{ color: '#e5e7eb', fontSize: 13, flex: 1, textTransform: 'capitalize' }}>
        {String(event.eventType || '').replace(/_/g, ' ')}
        {event.summary && <span style={{ color: '#9ca3af', marginLeft: 8, fontWeight: 400, textTransform: 'none' }}>{event.summary.replace(/Acct:\s*\S+/g, m => 'Acct: ***' + m.slice(-4))}</span>}
      </span>
      {event.durationMs != null && (
        <span style={{ color: '#6b7280', fontSize: 12, fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{fmtDuration(event.durationMs)}</span>
      )}
    </div>
    {expanded && event.detail && Object.keys(event.detail).length > 0 && (
      <div style={{ margin: '4px 0 8px 20px', padding: '10px 14px', background: '#0f172a', borderRadius: 6, border: '1px solid #1f2937' }}>
        <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 6 }}>
          {event.timestamp && <span>{fmtDate(event.timestamp)} ET</span>}
          {symbol && <span style={{ marginLeft: 12 }}>Symbol: {symbol}</span>}
        </div>
        <pre style={{ color: '#d1d5db', fontSize: 12, margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'monospace' }}>
          {JSON.stringify(maskSensitive(event.detail), null, 2)}
        </pre>
      </div>
    )}
  </div>
);

const OptionsAuditV2: React.FC = () => {
  const { user, token } = useAuth();
  const [runs, setRuns] = useState<RunSummary[]>([]);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [expandedEvent, setExpandedEvent] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [engineFilter, setEngineFilter] = useState<string>('all');

  const enabled = user?.optionsAutoTradingEnabled;

  const fetchData = useCallback(async () => {
    if (!token || !enabled) return;
    setLoading(true);
    try {
      const res = await getAudit(token, 300);
      setRuns(res.runs || []);
    } catch { }
    setLoading(false);
  }, [token, enabled]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Engine filter options come from the runs themselves (no extra request).
  const engines = React.useMemo(() => {
    const m = new Map<string, string>();
    for (const r of runs) if (r.engineId && !m.has(r.engineId)) m.set(r.engineId, engineDisplayName({ engineName: r.engineName, symbol: r.symbol || undefined, strategy: r.strategy, engineId: r.engineId }));
    return Array.from(m, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [runs]);
  const engineName = (id: string) => engines.find(e => e.id === id)?.name || (id || '').slice(0, 8);

  const filteredRuns = engineFilter === 'all' ? runs : runs.filter(r => r.engineId === engineFilter);
  const selectedRun = filteredRuns.find(r => r.runId === selectedRunId);
  const selectedEvents = [...(selectedRun?.events || [])].sort((a, b) => String(a.timestamp || '').localeCompare(String(b.timestamp || '')));

  // Stats ("today" is the ET calendar date, matching the ET timestamps shown)
  const today = etDate(new Date());
  const todayRuns = filteredRuns.filter(r => r.startedAt && etDate(r.startedAt) === today);
  const todayTrades = todayRuns.reduce((s, r) => s + r.trades, 0);
  const todaySkips = todayRuns.reduce((s, r) => s + r.skips, 0);
  const todayErrors = todayRuns.reduce((s, r) => s + r.errors, 0);

  if (!enabled) {
    return (
      <div className="dashboard-container"><Navigation />
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '24px 16px' }}>
          <h1 style={{ color: '#e5e7eb', margin: 0, fontSize: 22 }}>TT Options Audit Log v2</h1>
          <div style={{ background: '#1f2937', border: '1px solid #374151', borderRadius: 10, padding: 28, textAlign: 'center', marginTop: 16 }}>
            <p style={{ color: '#9ca3af' }}>This feature is not enabled.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container"><Navigation />
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '24px 16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h1 style={{ color: '#e5e7eb', margin: 0, fontSize: 22 }}>TT Options Audit Log v2</h1>
        <button onClick={fetchData} style={{ background: '#1f2937', color: '#9ca3af', border: '1px solid #374151', borderRadius: 6, padding: '6px 14px', cursor: 'pointer', fontSize: 13 }}>
          Refresh
        </button>
      </div>

      {/* Stats bar */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 20 }}>
        {[
          { label: 'Runs Today', value: todayRuns.length, color: '#3b82f6' },
          { label: 'Trades', value: todayTrades, color: todayTrades > 0 ? '#16a34a' : '#6b7280' },
          { label: 'Skipped', value: todaySkips, color: '#d97706' },
          { label: 'Errors', value: todayErrors, color: todayErrors > 0 ? '#dc2626' : '#6b7280' },
        ].map(s => (
          <div key={s.label} style={{ background: '#111827', borderRadius: 8, padding: '12px 20px', border: '1px solid #1f2937', flex: 1, textAlign: 'center' }}>
            <div style={{ color: s.color, fontSize: 24, fontWeight: 700 }}>{s.value}</div>
            <div style={{ color: '#9ca3af', fontSize: 12, marginTop: 2 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {loading && <p style={{ color: '#6b7280' }}>Loading audit log...</p>}

      {/* Two-panel layout */}
      <div style={{ display: 'flex', gap: 16, minHeight: 500 }}>
        {/* Left: Run list */}
        <div style={{ width: 320, flexShrink: 0 }}>
          <div style={{ marginBottom: 12 }}>
            <select value={engineFilter} onChange={e => { setEngineFilter(e.target.value); setSelectedRunId(null); }}
              style={{ width: '100%', background: '#111827', color: '#e5e7eb', border: '1px solid #374151', borderRadius: 6, padding: '8px 12px', fontSize: 13 }}>
              <option value="all">All Engines</option>
              {engines.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          </div>

          {filteredRuns.length === 0 && !loading && (
            <div style={{ color: '#6b7280', fontSize: 13, textAlign: 'center', padding: 20 }}>No runs found</div>
          )}

          <div style={{ maxHeight: 600, overflowY: 'auto' }}>
            {filteredRuns.map(run => (
              <RunCard key={run.runId} run={run} selected={selectedRunId === run.runId}
                onClick={() => { setSelectedRunId(run.runId); setExpandedEvent(null); }}
                engineName={engineName(run.engineId)} />
            ))}
          </div>
        </div>

        {/* Right: Event timeline */}
        <div style={{ flex: 1, background: '#111827', borderRadius: 10, border: '1px solid #1f2937', padding: '16px 20px', minHeight: 400 }}>
          {!selectedRun ? (
            <div style={{ color: '#6b7280', fontSize: 14, textAlign: 'center', marginTop: 100 }}>Select a run to view events</div>
          ) : (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div>
                  <span style={{ color: '#e5e7eb', fontSize: 16, fontWeight: 600 }}>{fmtDate(selectedRun.startedAt)}</span>
                  {selectedRun.symbol && <span style={{ color: '#a78bfa', marginLeft: 10 }}>{selectedRun.symbol}</span>}
                  <span style={{ color: '#6b7280', marginLeft: 10, fontSize: 12 }}>{engineName(selectedRun.engineId)}</span>
                </div>
                <div style={{ fontSize: 12, color: '#6b7280' }}>{selectedEvents.length} events</div>
              </div>
              {selectedEvents.map(e => (
                <EventRow key={e.eventId} event={e}
                  expanded={expandedEvent === e.eventId}
                  onClick={() => setExpandedEvent(expandedEvent === e.eventId ? null : e.eventId)} symbol={selectedRun.symbol} />
              ))}
            </>
          )}
        </div>
      </div>
      <RiskDisclaimer />
    </div>
    </div>
  );
};

export default OptionsAuditV2;
